import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'
import { useAtomValue } from 'jotai'
import DOMPurify from 'dompurify'
import { Header } from 'components/common/header/Header'
import { Spacer } from 'wiz-front-shared/components/Spacer'
import { noticeStore } from 'store/noticeStore'
import { getServiceId } from 'common/service'
import './NoticeDetail.css'

interface NoticeAttachment {
    fileId: number
    originalFileName: string
    publicUrl?: string
}

interface NoticeDetailItem {
    noticeNo: number
    title: string
    content: string
    regTime: string
    writerName?: string
    attachments?: NoticeAttachment[]
}

const formatDateTime = (dateString: string) => {
    if (!dateString) return ''

    const date = new Date(dateString)
    if (Number.isNaN(date.getTime())) return dateString

    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')

    return `${year}.${month}.${day} ${hours}:${minutes}`
}

export const NoticeDetail = () => {
    const { t } = useTranslation()
    const [searchParams] = useSearchParams()
    const noticeNo = Number(searchParams.get('noticeNo'))
    const isInvalidNoticeNo = Number.isNaN(noticeNo) || noticeNo <= 0
    const serviceId = getServiceId()
    const noticeDetailState = useAtomValue(noticeStore.noticeDetail.stateAtom)

    useEffect(() => {
        if (!isInvalidNoticeNo) {
            noticeStore.getNoticeDetail(noticeNo, serviceId)
        }
    }, [isInvalidNoticeNo, noticeNo, serviceId])

    useEffect(() => {
        return () => {
            noticeStore.noticeDetail.onDefault()
        }
    }, [])

    const detailData = noticeDetailState?.data?.data?.item
        || noticeDetailState?.data?.item
        || noticeDetailState?.data?.data
        || noticeDetailState?.data

    const notice: NoticeDetailItem | undefined = useMemo(() => {
        if (!detailData) return undefined

        return {
            noticeNo: Number(detailData.noticeNo ?? detailData.notice_id ?? 0),
            title: detailData.title ?? detailData.notice_title ?? '',
            content: detailData.content ?? detailData.notice_content ?? '',
            regTime: detailData.regTime ?? detailData.regDt ?? detailData.reg_date ?? '',
            writerName: detailData.writerName ?? detailData.writer ?? '',
            attachments: Array.isArray(detailData.attachments)
                ? detailData.attachments.map((attachment: any) => ({
                    fileId: Number(attachment.fileId ?? 0),
                    originalFileName: attachment.originalFileName ?? attachment.fileName ?? '',
                    publicUrl: attachment.publicUrl ?? attachment.fileUrl ?? '',
                }))
                : [],
        }
    }, [detailData])

    const sanitizedContent = DOMPurify.sanitize(notice?.content || '')
    const loadingText = t('notice.detail.loading', { defaultValue: '공지사항을 불러오는 중입니다.' })
    const invalidText = t('notice.detail.invalid', { defaultValue: '잘못된 공지사항 번호입니다.' })
    const notFoundText = t('notice.detail.notFound', { defaultValue: '공지사항 정보를 찾을 수 없습니다.' })
    const attachmentsText = t('notice.detail.attachments', { defaultValue: '첨부파일' })

    return (
        <div className="notice-detail-wrapper">
            <Header title={t('notice.title')} isBack />

            {isInvalidNoticeNo && (
                <div className="notice-detail-empty">{invalidText}</div>
            )}

            {!isInvalidNoticeNo && noticeDetailState.isPending && (
                <div className="notice-detail-empty">{loadingText}</div>
            )}

            {!isInvalidNoticeNo && (noticeDetailState.isError || !notice) && !noticeDetailState.isPending && (
                <div className="notice-detail-empty">{notFoundText}</div>
            )}

            {!isInvalidNoticeNo && notice && !noticeDetailState.isPending && (
                <div className="notice-detail-content">
                    <div className="notice-detail-card">
                        <span className="notice-detail-title">{notice.title}</span>
                        <Spacer height={8} />
                        <div className="notice-detail-meta">
                            <span>{formatDateTime(notice.regTime)}</span>
                            {notice.writerName && <span>{notice.writerName}</span>}
                        </div>
                        <Spacer height={20} />
                        <div
                            className="notice-detail-body"
                            dangerouslySetInnerHTML={{ __html: sanitizedContent }}
                        />
                    </div>

                    {!!notice.attachments?.length && (
                        <div className="notice-detail-card">
                            <span className="notice-detail-section-title">{attachmentsText}</span>
                            <Spacer height={12} />
                            <div className="notice-detail-attachments">
                                {notice.attachments.map((attachment) => (
                                    attachment.publicUrl ? (
                                        <a
                                            key={attachment.fileId}
                                            className="notice-detail-attachment"
                                            href={attachment.publicUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            {attachment.originalFileName}
                                        </a>
                                    ) : (
                                        <span key={attachment.fileId} className="notice-detail-attachment">
                                            {attachment.originalFileName}
                                        </span>
                                    )
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}
